// Single money formatter for the settlement surfaces. Uses Intl so negative
// amounts render "-$12.34" (not the hand-rolled "$-12.34") and grouping is
// locale-correct. Replaces the per-file `fmt` helpers.
//
// Cents ALWAYS render, even at .00. Money that sometimes shows cents and
// sometimes does not is the harder thing to scan down a tabular column, and
// every expected figure in the design reference is written to two decimals.
// /* §1.5 formatting rules — money */
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCents(cents: number): string {
  return USD.format((cents ?? 0) / 100);
}

// Processor fees get their own name because they are the one figure on these
// surfaces that routinely lands in odd cents ($203.35), and a fee-only change
// then has exactly one place to happen. It DELEGATES rather than building a
// second Intl instance: both are pinned to two decimals, and two formatters
// configured identically are two things that can drift apart silently.
// /* §1.5 formatting rules — processor fees */
export function formatFeeCents(cents: number): string {
  return formatCents(cents);
}

// Every feed meta, section count and consequence message reads a count through
// here, so "1 items" cannot reach a screen. The default plural is the naive
// "+s" that covers item / invoice / lot / bidder; anything that needs a real
// plural passes one. /* §1.5 formatting rules — counts */
export function countLabel(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

// Meta lines are an ARRAY of facts on the entry model and are joined only here,
// at render time. Never pre-join in data: a surface that wants to drop one fact
// has to be able to drop it, not perform string surgery on a finished line.
// /* §1.5 formatting rules — meta lines */
export const META_SEPARATOR = ' · ';

export function metaLine(parts: ReadonlyArray<string | null | undefined>): string {
  return parts.map((part) => part?.trim() ?? '').filter((part) => part.length > 0).join(META_SEPARATOR);
}


// The absolute half of a deadline meta — `Aug 27, 2026 · 12:15 AM`. The
// relative reading (`expiresInLabel`) says how long an admin has; this says
// which moment, and a surface that shows one without the other either can't be
// planned around or can't be checked against a calendar. Rendered in the
// admin's own zone: the deadline the server enforces is an instant, and an
// admin acts on it in the time they are actually in.
// /* §3 outstanding offer — {expiresAtLabel} */
const DATE_PART = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const TIME_PART = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

export function dateTimeLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return metaLine([DATE_PART.format(at), TIME_PART.format(at)]);
}

// The day an auction closed, as the console header's facts line reads it
// ("Closed Oct 18"). Rendered in the AUCTION's zone, not the admin's: the close
// is an event on the organization's calendar, and an admin reading it from
// another zone wants the day it happened there. An unknown zone falls back to
// the admin's own rather than rendering nothing. /* §4.1 header — facts line */
const CLOSED_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

export function closedDateLabel(iso: string, timeZone: string | null): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { ...CLOSED_DAY, ...(timeZone ? { timeZone } : {}) }).format(at);
  } catch {
    return new Intl.DateTimeFormat('en-US', CLOSED_DAY).format(at);
  }
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Below two days an admin thinks in hours ("expires in 30 hours" is a shift
// away, "expires in 1 day" is not); above it, days.
const HOURS_CEILING_MS = 2 * DAY_MS;

// The clock on an outstanding offer, as the feed row and the drawer's offer
// section both read it. Truncated rather than rounded: an offer with 5h59m left
// has five hours on it, and rounding up is the direction that makes an admin
// late. `now` is injected so a surface can render a stable frame and a test can
// assert an exact figure.
export function expiresInLabel(expiresAt: string, now: Date = new Date()): string {
  const remainingMs = new Date(expiresAt).getTime() - now.getTime();
  if (!Number.isFinite(remainingMs)) return '';
  if (remainingMs <= 0) return 'Expired';
  if (remainingMs < MINUTE_MS) return 'Expires in under a minute';
  if (remainingMs < HOUR_MS) return `Expires in ${countLabel(Math.floor(remainingMs / MINUTE_MS), 'minute')}`;
  if (remainingMs < HOURS_CEILING_MS) return `Expires in ${countLabel(Math.floor(remainingMs / HOUR_MS), 'hour')}`;
  return `Expires in ${countLabel(Math.floor(remainingMs / DAY_MS), 'day')}`;
}

// The relative half of the custody trail's `{relative} · {absolute}` meta. The
// absolute half is `dateTimeLabel` above — one formatter for an instant, so the
// trail and the offer deadline cannot describe the same moment two ways.
//
// Past instants only, which is every custody event: the trail records what has
// already happened. `now` is injected so a surface renders one stable frame and
// a test asserts an exact phrase. /* §6.2 custody timeline — meta line */
const RELATIVE = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

export function relativeLabel(at: string, now: Date = new Date()): string {
  const elapsedMs = now.getTime() - new Date(at).getTime();
  if (!Number.isFinite(elapsedMs)) return '';
  if (elapsedMs < MINUTE_MS) return 'just now';
  if (elapsedMs < HOUR_MS) return RELATIVE.format(-Math.floor(elapsedMs / MINUTE_MS), 'minute');
  if (elapsedMs < DAY_MS) return RELATIVE.format(-Math.floor(elapsedMs / HOUR_MS), 'hour');
  return RELATIVE.format(-Math.floor(elapsedMs / DAY_MS), 'day');
}

// How a lot names itself, everywhere one is named: the items table's second
// line, the item drawer's header, a feed row's meta. The prefix lives here so
// "Lot 33" cannot become "lot #33" one surface over. Null when the line is not
// a lot at all (donation / fee-coverage), which is a reason to render nothing
// rather than a reason to render "Lot null".
// /* §2.6 Item column · §4 drawer header */
export function lotLabel(lotNumber: number | null): string | null {
  return lotNumber === null ? null : `Item ${lotNumber}`;
}

// The noun every settlement surface uses for a lot the admin can place: an item,
// or a bundle sold as one. Keyed off which id the line carries, never off the
// title. A bundle reads as "Reassign bundle" so nobody mistakes the action for
// moving one member. Anything that names no lot (donation / fee lines, a null)
// reads as an item, the generic noun.
export type LotNoun = 'item' | 'bundle';

export function lotNoun(lot: { bundleId: number | null } | null | undefined): LotNoun {
  return lot?.bundleId != null ? 'bundle' : 'item';
}

export function capitalizedLotNoun(lot: { bundleId: number | null } | null | undefined): 'Item' | 'Bundle' {
  return lotNoun(lot) === 'bundle' ? 'Bundle' : 'Item';
}
